// frontend/src/core/auth/RequireAdminPermission.tsx
//
// Route guard for super-admin pages. Renders children if the caller has the
// permission, otherwise renders a friendly forbidden card.

import * as React from 'react';
import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { VISUAL } from '../../design-system/visual';
import { useAdminPermission } from './useAdminPermission';

export function RequireAdminPermission({
  permission,
  children,
}: {
  permission: string;
  children: React.ReactNode;
}): JSX.Element {
  const allowed = useAdminPermission(permission);
  if (allowed) return <>{children}</>;
  return (
    <div className="px-6 sm:px-8 lg:px-10 py-16">
      <div className={[VISUAL.bento, 'py-14 px-8 text-center max-w-lg mx-auto'].join(' ')}>
        <div className="mx-auto mb-5 h-14 w-14 rounded-2xl bg-coral/15 ring-1 ring-coral/25 flex items-center justify-center">
          <Lock className="h-6 w-6 text-coral" strokeWidth={1.6} />
        </div>
        <p className="text-[18px] font-semibold tracking-[-0.01em] text-foreground">
          You don't have access to this page.
        </p>
        <p className="text-[13.5px] text-muted-foreground mt-1.5 leading-relaxed">
          The <code className="font-mono text-[12.5px]">{permission}</code> permission is required.
          Ask an Owner to grant it from Access → Admins.
        </p>
        <Link
          to="/admin"
          className="mt-6 inline-flex items-center justify-center h-10 px-5 rounded-full bg-foreground text-background text-[13px] font-semibold"
        >
          Back to admin home
        </Link>
      </div>
    </div>
  );
}
